/**
 * Software mouse cursor for the anamorphic stereo split view in fullscreen.
 *
 * On an SBS 3D display the OS cursor is part of the frame like everything
 * else: it shows in ONE eye only, moves 2x fast after the per-eye stretch,
 * hops eyes at the center line, and over a mirrored overlay it sits on the
 * display-only copy (ftgs-stereo-mirror.ts) rather than the original that
 * takes input. So while active, the OS cursor is replaced:
 *
 *   - The cursor is drawn into BOTH halves at HALF the real mouse x
 *     (squeezed like the overlays). After the display stretches each half
 *     2x, both eyes see it exactly where the mouse really is - one fused
 *     cursor that moves 1:1 with the mouse, edge to edge, no hop at the
 *     center. This needs the canvas to fill the screen, hence fullscreen.
 *     The logical position is therefore just the real pointer position in
 *     container CSS px: no Pointer Lock, no click to start, nothing for a
 *     focus change (e.g. the display switching into 3D) to break.
 *   - The OS cursor is hidden with CSS (cursor: none under the
 *     ftgs-stereo-cursor-active root class) and disarmed: real (trusted)
 *     pointer/mouse/wheel events are swallowed at the window, their default
 *     actions (native slider drags, focus, text selection) cancelled, and
 *     the overlays' real :hover styles are switched off under the same root
 *     class - the hidden pointer's spot is NOT where the user sees the
 *     cursor. They are re-dispatched as synthetic events at the logical
 *     position:
 *       + over an overlay: the original's physical point in its own half,
 *         so the overlays' own geometry (seek bar, sliders) works unchanged
 *       + over the scene: the logical point, so the camera controller's
 *         offsetX deltas / focal picks behave as in the normal view.
 *     Hover is emulated with pointerover/out/enter/leave plus the
 *     ftgs-stereo-hover class, and the browser's implicit pointer capture
 *     with a capture target held for a drag.
 *   - Native range sliders ignore synthetic pointer events, so a press or
 *     drag on one sets its value from the cursor position directly.
 *   - Text fields get no native caret placement or selection from synthetic
 *     events either (2026-10-26): a press puts the caret at the character
 *     boundary under the cursor, a drag selects from there, and a double
 *     click selects the whole value - see caretIndexAt(). The selection only
 *     shows in the original's half; the mirrored copy cannot take focus.
 *
 * The caller decides when it is on (main.ts: anamorphic stereo AND
 * fullscreen) - on means active at once, off means the OS cursor is back.
 */

const HOVER_CLASS = 'ftgs-stereo-hover';

/** a second click on the same element within this window is a double click */
const DBLCLICK_MS = 500;

// arrow glyph - white with a dark outline so it reads over any scene
const CURSOR_SVG = '<svg viewBox="0 0 24 24" width="24" height="24"><path d="M3 2 L3 19 L7.5 14.5 L10.5 21.5 L13.5 20.2 L10.6 13.4 L17 13.4 Z" fill="#fff" stroke="#000" stroke-width="1.3" stroke-linejoin="round"/></svg>';
// <-> glyph, centered on the pointer - shown instead of the arrow over the
// Root Rotation X / Y / Z drag labels and while one is armed (CSS only, see
// ftgs-stereo-mirror.scss), as the OS ew-resize cursor is in the normal view
const RESIZE_SVG = '<svg class="ftgs-stereo-cursor-ew" viewBox="0 0 24 24" width="24" height="24"><path d="M1 12 L7 6 L7 10 L17 10 L17 6 L23 12 L17 18 L17 14 L7 14 L7 18 Z" fill="#fff" stroke="#000" stroke-width="1.3" stroke-linejoin="round"/></svg>';

// trusted events swallowed while active - re-dispatched at the cursor
const BLOCKED_EVENTS = [
    'pointermove', 'pointerdown', 'pointerup', 'pointercancel',
    'pointerover', 'pointerout', 'pointerenter', 'pointerleave',
    'mousemove', 'mousedown', 'mouseup', 'mouseover', 'mouseout',
    'click', 'auxclick', 'dblclick', 'contextmenu', 'wheel'
];

type Side = 'left' | 'right';

type SyntheticPointerInit = ConstructorParameters<typeof PointerEvent>[1];

/** the element and its ancestors, deepest first */
const chainOf = (el: Element | null): Element[] => {
    const chain: Element[] = [];
    for (let n = el; n; n = n.parentElement) {
        chain.push(n);
    }
    return chain;
};

class FtgsStereoCursor {
    private readonly container: HTMLElement;
    private readonly canvas: HTMLElement;

    private active = false;

    private layer: HTMLDivElement | null = null;
    private cursors: HTMLDivElement[] = [];

    // logical position, full-canvas CSS px relative to the container
    private x = 0;
    private y = 0;

    private buttons = 0;
    private hoverTarget: Element | null = null;
    private hoverMarked: Element[] = [];
    private captureTarget: Element | null = null;
    private sliderTarget: HTMLInputElement | null = null;
    // text field being clicked/dragged, and the caret index the press set
    private textTarget: HTMLInputElement | null = null;
    private textAnchor = 0;
    private measureContext: CanvasRenderingContext2D | null = null;
    private lastClick: { target: Element, time: number } | null = null;

    // last real pointer position, places the cursor on activation
    private lastClientX = 0;
    private lastClientY = 0;

    constructor(container: HTMLElement, canvas: HTMLElement) {
        this.container = container;
        this.canvas = canvas;

        window.addEventListener('pointermove', (event: PointerEvent) => {
            if (event.isTrusted && !this.active) {
                this.lastClientX = event.clientX;
                this.lastClientY = event.clientY;
            }
        }, { capture: true, passive: true });
    }

    /** on = the software cursor takes over at once; off = the OS cursor is back */
    setEnabled(enabled: boolean) {
        if (enabled) {
            this.activate();
        } else {
            this.deactivate();
        }
    }

    private activate() {
        if (this.active) {
            return;
        }
        this.active = true;
        console.log('[ftgs] stereo cursor: on');
        document.documentElement.classList.add('ftgs-stereo-cursor-active');
        this.createLayer();
        const rect = this.container.getBoundingClientRect();
        this.x = this.lastClientX - rect.left;
        this.y = this.lastClientY - rect.top;
        this.clamp(rect);
        BLOCKED_EVENTS.forEach((name) => {
            window.addEventListener(name, this.onTrustedEvent, { capture: true, passive: false });
        });
        this.updateCursor();
        // 켜지는 순간에는 호버를 흉내 내지 않음(2026-10-31): PC 화면에서 VR 아이콘을 눌러 창이
        // 글래스로 넘어오면 마지막 위치가 그 아이콘 자리라, 실제 마우스는 PC 화면에 있는데도
        // 재생바가 호버 중으로 잡혀 사라지지 않았음. 첫 실제 움직임부터 호버를 맞춤.
    }

    private deactivate() {
        if (!this.active) {
            return;
        }
        this.active = false;
        console.log('[ftgs] stereo cursor: off');
        document.documentElement.classList.remove('ftgs-stereo-cursor-active');
        BLOCKED_EVENTS.forEach((name) => {
            window.removeEventListener(name, this.onTrustedEvent, { capture: true });
        });
        // end any drag in progress and leave whatever was hovered
        if (this.buttons !== 0) {
            this.finishPress(0, null);
        }
        this.buttons = 0;
        this.captureTarget = null;
        this.sliderTarget = null;
        this.textTarget = null;
        this.setHoverTarget(null, null);
        this.layer?.remove();
        this.layer = null;
        this.cursors = [];
    }

    private createLayer() {
        const layer = document.createElement('div');
        layer.className = 'ftgs-stereo-layer ftgs-stereo-anamorphic ftgs-stereo-cursor-layer';
        for (const side of ['left', 'right'] as Side[]) {
            const half = document.createElement('div');
            half.className = `ftgs-stereo-half ftgs-stereo-half-${side}`;
            const inner = document.createElement('div');
            inner.className = 'ftgs-stereo-inner';
            const cursor = document.createElement('div');
            cursor.className = 'ftgs-stereo-cursor';
            cursor.innerHTML = CURSOR_SVG + RESIZE_SVG;
            inner.appendChild(cursor);
            half.appendChild(inner);
            layer.appendChild(half);
            this.cursors.push(cursor);
        }
        this.container.appendChild(layer);
        this.layer = layer;
    }

    private clamp(rect: DOMRect) {
        this.x = Math.max(0, Math.min(rect.width - 1, this.x));
        this.y = Math.max(0, Math.min(rect.height - 1, this.y));
    }

    private updateCursor() {
        const transform = `translate(${this.x}px, ${this.y}px)`;
        for (const cursor of this.cursors) {
            cursor.style.transform = transform;
            cursor.style.visibility = '';
        }
    }

    // --- trusted input -------------------------------------------------

    private readonly onTrustedEvent = (event: Event) => {
        if (!event.isTrusted || !this.active) {
            return;
        }
        event.stopImmediatePropagation();
        // the hidden pointer's own default actions would land on whatever
        // is under IT - a native slider drag, focus, text selection, the
        // context menu, page scroll
        if (event.type === 'wheel' || event.type === 'contextmenu' || event.type === 'pointerdown' || event.type === 'mousedown') {
            event.preventDefault();
        }

        if (event.type === 'pointermove') {
            const e = event as PointerEvent;
            const rect = this.container.getBoundingClientRect();
            this.moveTo(e.clientX - rect.left, e.clientY - rect.top, e);
        } else if (event.type === 'pointerout' && !(event as PointerEvent).relatedTarget) {
            // left the window (another monitor): hide the cursor and leave
            // whatever was hovered until the pointer comes back
            this.setHoverTarget(null, event as PointerEvent);
            for (const cursor of this.cursors) {
                cursor.style.visibility = 'hidden';
            }
        } else if (event.type === 'pointerdown') {
            const e = event as PointerEvent;
            this.press(e.button, e);
        } else if (event.type === 'pointerup') {
            const e = event as PointerEvent;
            this.release(e.button, e);
        } else if (event.type === 'wheel') {
            this.wheel(event as WheelEvent);
        }
    };

    /** move the cursor by a delta - the console test hook */
    moveBy(dx: number, dy: number, source?: MouseEvent) {
        this.moveTo(this.x + dx, this.y + dy, source);
    }

    /** move the cursor to a logical position (container CSS px) */
    moveTo(x: number, y: number, source?: MouseEvent) {
        const rect = this.container.getBoundingClientRect();
        const dx = x - this.x;
        const dy = y - this.y;
        this.x = x;
        this.y = y;
        this.clamp(rect);
        this.updateCursor();

        if (this.captureTarget) {
            this.dispatchPointer('pointermove', this.captureTarget, source, dx, dy);
            this.dispatchMouse('mousemove', this.captureTarget, source, dx, dy);
            if (this.sliderTarget) {
                this.setSliderFromCursor(this.sliderTarget);
            }
            if (this.textTarget) {
                const index = this.caretIndexAt(this.textTarget);
                const anchor = this.textAnchor;
                this.textTarget.setSelectionRange(Math.min(anchor, index), Math.max(anchor, index), index < anchor ? 'backward' : 'forward');
            }
        } else {
            const target = this.updateHover(source);
            if (target) {
                this.dispatchPointer('pointermove', target, source, dx, dy);
                this.dispatchMouse('mousemove', target, source, dx, dy);
            }
        }
    }

    press(button: number, source?: MouseEvent) {
        const target = this.captureTarget ?? this.updateHover(source);
        this.buttons |= [1, 4, 2, 8, 16][button] ?? 0;
        if (!target) {
            return;
        }
        // the first button down captures the pointer, like the browser's
        // implicit capture - the drag keeps going to the pressed element
        if (!this.captureTarget) {
            this.captureTarget = target;
            // the trusted pointerdown's default (focus) was cancelled, so a
            // text field - the stats panel's Edit Stereo Preset fields -
            // takes focus here; pressing anything else lets a focused one
            // go, which also commits it
            const focused = document.activeElement;
            if (target instanceof HTMLInputElement && target.type === 'text') {
                target.focus();
                if (button === 0) {
                    // caret at the press point, the anchor of a drag selection
                    this.textTarget = target;
                    this.textAnchor = this.caretIndexAt(target);
                    target.setSelectionRange(this.textAnchor, this.textAnchor);
                }
            } else if (focused instanceof HTMLInputElement && focused.type === 'text') {
                focused.blur();
            }
            if (target instanceof HTMLInputElement && target.type === 'range' && button === 0) {
                this.sliderTarget = target;
                this.setSliderFromCursor(target);
            }
        }
        this.dispatchPointer('pointerdown', target, source, 0, 0, button);
        this.dispatchMouse('mousedown', target, source, 0, 0, button);
    }

    release(button: number, source?: MouseEvent) {
        this.buttons &= ~([1, 4, 2, 8, 16][button] ?? 0);
        this.finishPress(button, source);
    }

    private finishPress(button: number, source: MouseEvent | null | undefined) {
        const pressed = this.captureTarget;
        if (!pressed) {
            return;
        }
        this.dispatchPointer('pointerup', pressed, source, 0, 0, button);
        this.dispatchMouse('mouseup', pressed, source, 0, 0, button);

        if (this.sliderTarget) {
            this.sliderTarget.dispatchEvent(new Event('change', { bubbles: true }));
        }

        if (this.buttons === 0) {
            this.captureTarget = null;
            this.sliderTarget = null;
            this.textTarget = null;
            // a click lands where press and release share the element
            const over = this.updateHover(source);
            if (over && button === 0 && (over === pressed || pressed.contains(over))) {
                this.dispatchMouse('click', pressed, source, 0, 0, button);
                const now = performance.now();
                if (this.lastClick && this.lastClick.target === pressed && now - this.lastClick.time < DBLCLICK_MS) {
                    this.dispatchMouse('dblclick', pressed, source, 0, 0, button);
                    // a native double click selects the field's text
                    if (pressed instanceof HTMLInputElement && pressed.type === 'text') {
                        pressed.select();
                    }
                    this.lastClick = null;
                } else {
                    this.lastClick = { target: pressed, time: now };
                }
            } else if (over && button === 2) {
                this.dispatchMouse('contextmenu', pressed, source, 0, 0, button);
            }
        }
    }

    private wheel(source: WheelEvent) {
        const target = this.captureTarget ?? this.updateHover(source);
        if (!target) {
            return;
        }
        const { clientX, clientY } = this.clientPointFor(target);
        const wheel = new WheelEvent('wheel', {
            bubbles: true,
            cancelable: true,
            composed: true,
            clientX,
            clientY,
            deltaX: source.deltaX,
            deltaY: source.deltaY,
            deltaZ: source.deltaZ,
            deltaMode: source.deltaMode,
            buttons: this.buttons,
            ctrlKey: source.ctrlKey,
            shiftKey: source.shiftKey,
            altKey: source.altKey,
            metaKey: source.metaKey
        });
        target.dispatchEvent(wheel);
        // A synthetic wheel event has no default action, so a scrollable
        // overlay under the cursor (the stats panel past the window height)
        // is scrolled here instead - unless a handler took the wheel.
        if (!wheel.defaultPrevented && !this.isScene(target)) {
            const scroller = this.scrollableAncestor(target);
            if (scroller) {
                const unit = source.deltaMode === WheelEvent.DOM_DELTA_LINE ? 16 :
                    source.deltaMode === WheelEvent.DOM_DELTA_PAGE ? scroller.clientHeight : 1;
                scroller.scrollTop += source.deltaY * unit;
            }
        }
    }

    /** nearest ancestor of `el` (itself included) that can scroll vertically */
    private scrollableAncestor(el: Element): HTMLElement | null {
        for (let n: Element | null = el; n && n !== this.container; n = n.parentElement) {
            if (n instanceof HTMLElement && n.scrollHeight > n.clientHeight) {
                const overflowY = getComputedStyle(n).overflowY;
                if (overflowY === 'auto' || overflowY === 'scroll') {
                    return n;
                }
            }
        }
        return null;
    }

    // --- hit testing ----------------------------------------------------

    private isScene(el: Element | null) {
        return !el || el === this.canvas || el === this.container || !this.container.contains(el);
    }

    /**
     * What the logical cursor is over. The same spot exists once per half;
     * the copies take no pointer, so whichever half holds the ORIGINAL of
     * an overlay there is what elementFromPoint finds. Neither -> the scene.
     */
    private hitTest(): Element {
        const rect = this.container.getBoundingClientRect();
        const y = rect.top + this.y;
        for (const offset of [0, rect.width / 2]) {
            const el = document.elementFromPoint(rect.left + offset + this.x / 2, y);
            if (!this.isScene(el)) {
                return el;
            }
        }
        return this.canvas;
    }

    /** synthetic event coordinates for a target - see the module doc */
    private clientPointFor(target: Element) {
        const rect = this.container.getBoundingClientRect();
        if (this.isScene(target)) {
            return { clientX: rect.left + this.x, clientY: rect.top + this.y };
        }
        const box = target.getBoundingClientRect();
        const side: Side = box.left + box.width / 2 < rect.left + rect.width / 2 ? 'left' : 'right';
        return {
            clientX: rect.left + (side === 'right' ? rect.width / 2 : 0) + this.x / 2,
            clientY: rect.top + this.y
        };
    }

    private updateHover(source?: MouseEvent | null): Element {
        const target = this.hitTest();
        if (target !== this.hoverTarget) {
            this.setHoverTarget(target, source);
        }
        return target;
    }

    /** emulate the boundary events and :hover for a hover change */
    private setHoverTarget(target: Element | null, source: MouseEvent | null | undefined) {
        const previous = this.hoverTarget;
        this.hoverTarget = target;
        const oldChain = chainOf(previous);
        const newChain = chainOf(target);

        if (previous) {
            this.dispatchPointer('pointerout', previous, source, 0, 0, 0, target);
            this.dispatchMouse('mouseout', previous, source, 0, 0, 0, target);
            oldChain.filter(el => !newChain.includes(el)).forEach((el) => {
                this.dispatchPointer('pointerleave', el, source, 0, 0, 0, target, false);
                this.dispatchMouse('mouseleave', el, source, 0, 0, 0, target, false);
            });
        }
        if (target) {
            this.dispatchPointer('pointerover', target, source, 0, 0, 0, previous);
            this.dispatchMouse('mouseover', target, source, 0, 0, 0, previous);
            newChain.filter(el => !oldChain.includes(el)).reverse().forEach((el) => {
                this.dispatchPointer('pointerenter', el, source, 0, 0, 0, previous, false);
                this.dispatchMouse('mouseenter', el, source, 0, 0, 0, previous, false);
            });
        }

        // :hover stand-in on the original overlay; the mirrors copy it
        const marked = this.isScene(target) ? [] : newChain.filter(el => this.container.contains(el) && el !== this.container);
        this.hoverMarked.filter(el => !marked.includes(el)).forEach(el => el.classList.remove(HOVER_CLASS));
        marked.forEach(el => el.classList.add(HOVER_CLASS));
        this.hoverMarked = marked;
    }

    // --- synthetic events -----------------------------------------------

    private eventInit(target: Element, source: MouseEvent | null | undefined, dx: number, dy: number, button: number, related: Element | null, bubbles: boolean): SyntheticPointerInit {
        const { clientX, clientY } = this.clientPointFor(target);
        return {
            bubbles,
            cancelable: true,
            composed: true,
            clientX,
            clientY,
            screenX: clientX,
            screenY: clientY,
            movementX: dx,
            movementY: dy,
            button,
            buttons: this.buttons,
            relatedTarget: related,
            ctrlKey: source?.ctrlKey ?? false,
            shiftKey: source?.shiftKey ?? false,
            altKey: source?.altKey ?? false,
            metaKey: source?.metaKey ?? false,
            view: window
        };
    }

    private dispatchPointer(type: string, target: Element, source: MouseEvent | null | undefined, dx = 0, dy = 0, button = 0, related: Element | null = null, bubbles = true) {
        target.dispatchEvent(new PointerEvent(type, {
            ...this.eventInit(target, source, dx, dy, button, related, bubbles),
            pointerId: 1,
            pointerType: 'mouse',
            isPrimary: true
        }));
    }

    private dispatchMouse(type: string, target: Element, source: MouseEvent | null | undefined, dx = 0, dy = 0, button = 0, related: Element | null = null, bubbles = true) {
        target.dispatchEvent(new MouseEvent(type, this.eventInit(target, source, dx, dy, button, related, bubbles)));
    }

    /**
     * The character boundary of a text field nearest the cursor - what a
     * native press would put the caret at. The field's box may be squeezed
     * (anamorphic SBS), so the cursor's offset into it is scaled back to the
     * field's own layout width before the padding, border and horizontal
     * scroll are taken off and the text is measured in its own font.
     */
    private caretIndexAt(input: HTMLInputElement): number {
        const box = input.getBoundingClientRect();
        const text = input.value;
        if (box.width <= 0 || text.length === 0) {
            return text.length;
        }
        const style = getComputedStyle(input);
        const { clientX } = this.clientPointFor(input);
        this.measureContext ??= document.createElement('canvas').getContext('2d');
        const ctx = this.measureContext;
        ctx.font = `${style.fontStyle} ${style.fontWeight} ${style.fontSize} ${style.fontFamily}`;
        // where the text starts inside the content box: the panel's number
        // fields are right-aligned, so a short value sits at the right edge
        // (a value wider than the field starts at the left and scrolls)
        const contentWidth = input.clientWidth - parseFloat(style.paddingLeft) - parseFloat(style.paddingRight);
        const slack = Math.max(0, contentWidth - ctx.measureText(text).width);
        const align = style.textAlign;
        const textStart = align === 'right' || align === 'end' ? slack : (align === 'center' ? slack / 2 : 0);
        const x = (clientX - box.left) / box.width * input.offsetWidth -
            parseFloat(style.borderLeftWidth) - parseFloat(style.paddingLeft) + input.scrollLeft - textStart;
        let best = 0;
        let bestDist = Math.abs(x);
        for (let i = 1; i <= text.length; i++) {
            const dist = Math.abs(x - ctx.measureText(text.slice(0, i)).width);
            if (dist < bestDist) {
                best = i;
                bestDist = dist;
            }
        }
        return best;
    }

    private setSliderFromCursor(slider: HTMLInputElement) {
        const box = slider.getBoundingClientRect();
        if (box.width <= 0) {
            return;
        }
        const { clientX } = this.clientPointFor(slider);
        const frac = Math.max(0, Math.min(1, (clientX - box.left) / box.width));
        const min = parseFloat(slider.min) || 0;
        const max = parseFloat(slider.max) || 100;
        const step = parseFloat(slider.step) || 1;
        // snap to the step grid the way the native slider does (anchored
        // at min), and print with the step's decimals so float noise like
        // 0.30000000000000004 never reaches the value
        const value = Math.max(min, Math.min(max, min + Math.round(frac * (max - min) / step) * step));
        const decimals = (String(step).split('.')[1] ?? '').length;
        const text = String(Number(value.toFixed(decimals)));
        if (slider.value !== text) {
            slider.value = text;
            slider.dispatchEvent(new Event('input', { bubbles: true }));
        }
    }
}

export { FtgsStereoCursor };
